import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Play } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import WaitlistForm from '@/components/sections/WaitlistForm';
import { COMPANIES, PHOTOS, STAN_STRATEGY_URL, CEO_INTELLIGENCE, CUSTOMER_JOURNEY, TIA_TEASER, COMPANY_LOGOS } from '@/constants/content';
import { COMPANY } from '@/constants/testIds';
import { useReveal } from '@/lib/useReveal';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

const WAITLIST_COPY = {
  'ceo-intelligence': {
    headline: 'Build a Business in 1 Hour with AI',
    body:
      'Be among the first to experience CEO Intelligence, the AI Business Builder designed to help aspiring entrepreneurs turn ideas into businesses with greater speed, clarity, and confidence.',
  },
  movemode: {
    headline: 'Movement, wellness, and intentional living.',
    body:
      'Move MODE is a lifestyle brand centered around movement, wellness, fitness, and intentional living. Join the waitlist to be notified when the lifestyle experience officially launches.',
  },
};

function CompanyRow({ company, i }) {
  const ref = useReveal();
  const reverse = i % 2 === 1;
  const isCEOI = company.slug === 'ceo-intelligence';
  const logoKey = typeof company.hasLogo === 'string' ? company.hasLogo : null;
  const brandLogo = logoKey ? COMPANY_LOGOS[logoKey] : null;
  const isDisabled = !!company.comingSoon;
  const isExternal = !!company.external;

  return (
    <article
      ref={ref}
      id={company.slug}
      data-testid={COMPANY.card(company.slug)}
      className="reveal grid lg:grid-cols-12 gap-12 lg:gap-20 items-center py-16 lg:py-24 border-t border-taupe/25 scroll-mt-32"
    >
      <div className={`lg:col-span-7 ${reverse ? 'lg:order-2' : ''}`}>
        <div className="flex items-baseline gap-6 mb-8">
          <span className="font-serif-display text-6xl md:text-7xl text-taupe/50 tracking-tighter leading-none">
            {String(i + 1).padStart(2, '0')}
          </span>
          <span className="hairline-gold h-px w-16" />
          <span className="eyebrow text-taupe">{company.stage}</span>
        </div>
        <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-matte">
          {company.name}
        </h2>
        <p className="mt-4 font-serif-display italic text-2xl md:text-3xl text-taupe max-w-xl leading-snug">
          {company.tagline}
        </p>
        <p className="mt-8 text-matte/75 max-w-lg leading-relaxed">{company.description}</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
          <div>
            <div className="eyebrow text-taupe mb-2">Mission</div>
            <p className="text-sm text-matte/75 leading-relaxed">{company.mission}</p>
          </div>
          <div>
            <div className="eyebrow text-taupe mb-2">Vision</div>
            <p className="text-sm text-matte/75 leading-relaxed">{company.vision}</p>
          </div>
          <div className="md:col-span-2">
            <div className="eyebrow text-taupe mb-2">Roadmap</div>
            <p className="text-sm text-matte/75 leading-relaxed">{company.roadmap}</p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          {isDisabled ? null : company.primaryCta ? (
            <Link
              to={company.primaryCta.to}
              data-testid={COMPANY.visit(company.slug)}
              className="btn-primary"
            >
              {company.primaryCta.label}
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          ) : (
            <a
              href={company.url}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noreferrer' : undefined}
              data-testid={COMPANY.visit(company.slug)}
              className="btn-ghost"
            >
              Visit {company.name}
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
          )}
          {company.secondaryCta && (
            <Link to={company.secondaryCta.to} className="btn-ghost" data-testid={`company-cta-secondary-${company.slug}`}>
              {company.secondaryCta.label}
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          )}
          {company.tertiaryCta && (
            <a
              href={company.tertiaryCta.href}
              target={company.tertiaryCta.external ? '_blank' : undefined}
              rel={company.tertiaryCta.external ? 'noreferrer' : undefined}
              className="btn-ghost"
              data-testid={`company-cta-tertiary-${company.slug}`}
            >
              <Play size={12} strokeWidth={1.6} className="mr-0.5" />
              {company.tertiaryCta.label}
            </a>
          )}
        </div>

        {isDisabled && WAITLIST_COPY[company.slug] && (
          <div className="mt-10 pt-10 border-t border-taupe/25">
            <WaitlistForm
              product={company.slug === 'ceo-intelligence' ? 'ceo-intelligence' : 'move-mode'}
              productLabel={company.name}
              headline={WAITLIST_COPY[company.slug].headline}
              body={WAITLIST_COPY[company.slug].body}
              variant="light"
            />
          </div>
        )}
      </div>
      <div className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
        {isCEOI ? (
          <div className="rounded-3xl overflow-hidden bg-matte aspect-[4/5] flex flex-col items-center justify-center p-12 luxury-shadow relative">
            <div className="absolute inset-0 opacity-20 noise-grain pointer-events-none" aria-hidden />
            <img
              src={CEO_INTELLIGENCE.logoOnDark}
              alt="CEO Intelligence logo"
              className="relative w-full max-w-xs object-contain"
              loading="lazy"
              data-testid="company-logo-ceo-intelligence"
            />
            <div className="mt-8 hairline-gold h-px w-16" />
            <div className="mt-6 eyebrow text-gold">AI Business Builder</div>
          </div>
        ) : brandLogo ? (
          <div className={`rounded-3xl overflow-hidden aspect-[4/5] flex flex-col items-center justify-center p-10 md:p-12 luxury-shadow relative ${logoKey === 'labfounders' || logoKey === 'betweenus' ? 'bg-matte' : 'bg-white border border-taupe/25'}`}>
            {(logoKey === 'labfounders' || logoKey === 'betweenus') && (
              <div className="absolute inset-0 opacity-20 noise-grain pointer-events-none" aria-hidden />
            )}
            <img
              src={brandLogo}
              alt={`${company.name} logo`}
              className="relative w-full max-w-[280px] object-contain"
              loading="lazy"
              data-testid={`company-logo-${company.slug}`}
            />
            <div className="mt-8 hairline-gold h-px w-16" />
            <div className={`mt-6 eyebrow ${logoKey === 'labfounders' || logoKey === 'betweenus' ? 'text-gold' : 'text-taupe'}`}>{company.stage}</div>
          </div>
        ) : (
          <div className="rounded-3xl overflow-hidden bg-sand/50 aspect-[4/5] flex items-center justify-center p-12">
            <div className="text-center">
              <div className="font-serif-display italic text-4xl md:text-5xl text-taupe leading-tight tracking-tight">{company.name}</div>
              <div className="mt-6 hairline-gold h-px w-16 mx-auto" />
              <div className="mt-6 eyebrow text-taupe/80">{company.stage}</div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function CustomerJourney() {
  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="companies-customer-journey">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
        <div className="max-w-3xl mb-16">
          <div className="eyebrow text-taupe mb-6">The Customer Journey</div>
          <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
            From idea to launch, and beyond.
          </h3>
          <p className="mt-8 text-matte/70 leading-relaxed max-w-2xl">
            CEO Intelligence is the starting point for every entrepreneur. Once your business is
            live, our industry-specific platforms provide the specialized infrastructure to scale.
          </p>
        </div>

        <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-stretch">
          {CUSTOMER_JOURNEY.map((step, idx) => (
            <li key={step.label} className={`relative bg-ivory rounded-2xl p-8 border ${idx === 1 ? 'border-gold/60' : 'border-taupe/20'} luxury-shadow`}>
              <div className={`eyebrow mb-4 ${idx === 1 ? 'text-gold' : 'text-taupe'}`}>{String(idx + 1).padStart(2, '0')} · Step</div>
              <div className="font-serif-display text-3xl leading-tight tracking-tightest text-matte mb-3">{step.label}</div>
              <p className="text-sm text-matte/70 leading-relaxed">{step.body}</p>
              {idx < CUSTOMER_JOURNEY.length - 1 && (
                <ArrowRight size={20} strokeWidth={1.2} className="hidden lg:block absolute -right-6 top-1/2 -translate-y-1/2 text-taupe/50" aria-hidden />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CEOInAction() {
  const s = CEO_INTELLIGENCE.summit;
  return (
    <section className="py-16 lg:py-24 bg-matte text-ivory relative overflow-hidden" data-testid="companies-ceoi-in-action">
      <div className="absolute inset-0 opacity-25 noise-grain pointer-events-none" aria-hidden />
      <div className="relative mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5">
          <div className="eyebrow text-gold mb-6">See CEO Intelligence in Action</div>
          <h3 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02]">
            Watch the framework, <em className="italic text-gold">live.</em>
          </h3>
          <p className="mt-8 text-ivory/75 leading-[1.85] max-w-lg">
            Rather than simply reading about CEO Intelligence, watch the framework presented live
            during the AI in Action Virtual Summit.
          </p>
          <div className="mt-10 space-y-2">
            <div className="eyebrow text-gold">Featured Presentation</div>
            <div className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight text-ivory">
              {s.title}
            </div>
            <div className="font-label text-[11px] tracking-widest uppercase text-ivory/60 pt-2">{s.event}</div>
          </div>
          <div className="mt-10">
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer"
              data-testid="companies-ceoi-watch-youtube"
              className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte"
            >
              <Play size={14} strokeWidth={1.6} />
              Watch on YouTube
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <a
            href={s.url}
            target="_blank"
            rel="noreferrer"
            aria-label="Watch CEO Intelligence: How to Build a Business in 1 Hour with AI on YouTube"
            data-testid="companies-ceoi-video-preview"
            className="group relative block rounded-3xl overflow-hidden luxury-shadow"
          >
            <img
              src={s.thumbnail}
              onError={(e) => { e.currentTarget.src = s.thumbnailFallback; }}
              alt="CEO Intelligence Summit presentation preview"
              className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-matte/70 via-matte/10 to-transparent pointer-events-none" aria-hidden />
            <div className="absolute inset-0 grid place-items-center">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gold grid place-items-center transition-transform duration-500 group-hover:scale-110 shadow-[0_20px_60px_-15px_rgba(200,161,90,0.7)]">
                <Play size={30} strokeWidth={1.4} className="text-matte translate-x-0.5" />
              </span>
            </div>
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-ivory">
              <div>
                <div className="eyebrow text-gold mb-1">YouTube · Free</div>
                <div className="font-serif-display italic text-xl md:text-2xl leading-tight tracking-tight">
                  Watch the summit presentation
                </div>
              </div>
              <ArrowUpRight size={22} strokeWidth={1.2} className="text-ivory/80 group-hover:text-gold transition-colors" />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function TIATeaser() {
  return (
    <section className="py-16 lg:py-24" data-testid="companies-tia-teaser">
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-20">
        <div className="rounded-3xl bg-ivory border border-gold/30 p-10 md:p-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow text-gold mb-6">{TIA_TEASER.eyebrow}</div>
            <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tightest leading-[1.02] text-matte">
              {TIA_TEASER.title}
            </h3>
            <p className="mt-6 text-matte/70 leading-relaxed max-w-2xl">{TIA_TEASER.body}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full bg-gold/15 border border-gold grid place-items-center">
              <span className="font-serif-display text-3xl lg:text-5xl italic text-gold">TIA</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Companies() {
  usePageMeta({ ...SEO.companies });
  return (
    <div className="bg-ivory">
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">Companies · The Ecosystem</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              An ecosystem
              <br />
              of <em className="italic text-taupe">quiet ventures.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">
              A composed portfolio of technology, media, education, and real estate companies,
              each designed to help a specific founder build with intention.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="rounded-3xl overflow-hidden luxury-shadow">
            <img src={PHOTOS.companies} alt="Toia Wallace, companies portfolio" className="w-full h-[70vh] lg:h-[86vh] object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="eyebrow text-taupe mb-4">The Portfolio</div>
          {COMPANIES.map((c, i) => (
            <div key={c.slug}>
              <CompanyRow company={c} i={i} />
              {c.slug === 'ceo-intelligence' && (
                <>
                  <CustomerJourney />
                  <CEOInAction />
                  <TIATeaser />
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-matte text-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 text-center">
          <div className="eyebrow text-gold mb-8">Partner · Invest · Advise</div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-tight">
            Interested in one of the ventures?
          </h2>
          <p className="mt-6 text-ivory/70 max-w-xl mx-auto">
            For partnerships, investment conversations, or strategic collaborations, start a
            conversation with the studio.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href="/contact" className="btn-primary bg-gold text-matte hover:bg-[#b8944e]" data-testid="companies-cta-contact">
              Start a Conversation
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
            <a href={STAN_STRATEGY_URL} target="_blank" rel="noreferrer" className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte" data-testid="companies-cta-strategy">
              Book a Strategy Intensive
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
